import "./SettingBefore.scss";
import { EmptyPlaceholder, Iconfont } from "@eweadn-qiankun/shared-components";
import { useTranslation } from "react-i18next";
import { useKeyboardTest } from "./useKeyboardTest";

interface SettingBeforeProps {
  advancedKeyAllData: any[];
  advancedKeyAfterList?: any[];
  getCheckData: (data: any) => void;
  getUpdate: (data: any, key: number) => void;
  onDelete: (data: any, key: number) => void;
  /** 预览场景不展示按键测试 */
  showKeyTest?: boolean;
  /** 预览场景：点击已配置项查看详情 */
  previewMode?: boolean;
  onViewItem?: (data: any, key: number) => void;
}

const settingBefore = (props: SettingBeforeProps) => {
  const { t } = useTranslation();
  const {
    advancedKeyAllData,
    getCheckData,
    advancedKeyAfterList,
    getUpdate,
    onDelete,
    showKeyTest = true,
    previewMode = false,
    onViewItem,
  } = props;
  const checkData = async (data: any) => {
    getCheckData(data);
  };
  const setUpdate = async (data: any, key: number) => {
    getUpdate(data, key);
  };
  const setDelete = async (data: any, key: number) => {
    onDelete(data, key);
  };

  const { pressedKeys, releasedKeys, getTargetKeyLabel } = useKeyboardTest({
    enabled: showKeyTest,
  });

  return (
    <div
      className={`setting-before-container${
        showKeyTest ? "" : " setting-before-container--no-key-test"
      }`}
    >
      {/* 选择高级键模式 */}
      <div
        className="advance-setting-box setting-before-mode"
        data-ui-workshop-slot="advancedKeyBar"
      >
        <div className="advance-setting-box-header">
          {t("keyboard.advancedKey.selectModeTitle")}
        </div>
        <div className="setting-mode-list">
          {advancedKeyAllData.map((item: any, index: number) => {
            return (
              <div
                className="mode-list-item"
                key={index}
                onClick={() => checkData(item)}
              >
                {item.icon ? (
                  <Iconfont icon={item.icon} className="mode-icon" />
                ) : (
                  <div
                    className="mode-icon mode-icon--placeholder"
                    aria-hidden
                  />
                )}
                <div className="mode-label">
                  <div className="mode-label-title">
                    {item.titleKey ? t(item.titleKey) : item.title}
                  </div>
                  <div className="mode-label-desc">
                    {item.descKey ? t(item.descKey) : item.cn}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {/* 已配置高级键列表 */}
      <div
        className="advance-setting-box setting-before-selected"
        data-ui-workshop-slot="advancedKeyBar"
      >
        <div className="advance-setting-box-header">
          {t("keyboard.advancedKey.configuredKeys")}{" "}
          <span className="configured-keys-count">
            {advancedKeyAfterList?.length || 0}
          </span>
          /20
        </div>
        <div className="setting-advancedKey-list">
          {!advancedKeyAfterList?.length ? (
            <EmptyPlaceholder
              className="setting-advancedKey-empty"
              text={t("keyboard.advancedKey.emptyConfiguredKeys")}
            />
          ) : (
            advancedKeyAfterList.map((item: any, key: number) => {
              return (
                <div
                  className={`advancedKey-list-item${
                    previewMode ? " advancedKey-list-item--preview" : ""
                  }`}
                  key={key}
                  onClick={
                    previewMode ? () => onViewItem?.(item, key) : undefined
                  }
                >
                  <div className="list-item-left">
                    <div className="key-list">
                      <div className="advance-key-button">{item.label}</div>
                      {item.label2 ? (
                        <div className="advance-key-button">{item.label2}</div>
                      ) : null}
                    </div>
                    {item.icon ? (
                      <Iconfont icon={item.icon} className="mode-icon" />
                    ) : (
                      <div
                        className="mode-icon mode-icon--placeholder"
                        aria-hidden
                      />
                    )}
                    <div className="key-list">
                      {getTargetKeyLabel(item)?.map(
                        (label: string, i: number) => {
                          return (
                            <div
                              className="advance-key-button"
                              key={`${label}-${i}`}
                            >
                              {label}
                            </div>
                          );
                        }
                      )}
                    </div>
                  </div>
                  <div className="operation">
                    {previewMode ? (
                      <div className="btns btns--view">
                        <Iconfont
                          icon="view"
                          title={t("keyboard.advancedKey.viewDetails")}
                        />
                      </div>
                    ) : (
                      <>
                        <div
                          className="btns"
                          onClick={() => setUpdate(item, key)}
                        >
                          <Iconfont
                            icon="edit"
                            title={t("keyboard.advancedKey.edit")}
                          />
                        </div>
                        <div
                          className="btns"
                          onClick={() => setDelete(item, key)}
                        >
                          <Iconfont
                            icon="delete"
                            title={t("keyboard.advancedKey.delete")}
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
      {showKeyTest ? (
        <div
          className="advance-setting-box setting-before-test"
          data-ui-workshop-slot="advancedKeyBar"
        >
          <div className="advance-setting-box-header">
            {t("keyboard.advancedKey.keysTest")}
          </div>
          <div className="setting-test-list">
            <div className="test-button-list">
              <div className="test-button-list-header">
                {t("keyboard.advancedKey.pressedKeys")}
              </div>
              {pressedKeys.length > 0 ? (
                <div className="test-button-list-content">
                  {pressedKeys.map((k) => (
                    <div className="advance-key-button test-button" key={k.id}>
                      {k.label}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="test-button-empty">
                  <div className="empty-icon"></div>
                  <div className="empty-text">
                    {t("keyboard.advancedKey.pressedKeysDesc")}
                  </div>
                </div>
              )}
            </div>
            <div className="test-button-list">
              <div className="test-button-list-header">
                {t("keyboard.advancedKey.releasedKeys")}
              </div>
              {releasedKeys.length > 0 && (
                <div className="test-button-list-content">
                  {releasedKeys.map((k) => (
                    <div className="advance-key-button test-button" key={k.id}>
                      {k.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};
export default settingBefore;
